import React, { useCallback, useContext, useEffect, useState } from 'react'
import InlineTip from 'app/React/Components/misc/InlineTip'
import { getCurrentPrice, getSecurityDeposit } from 'app/React/Helpers/PriceHelper'
import Price from 'app/React/Components/Footer/Price'
import { getColorImage } from 'app/React/Helpers/ColorImageHelper'
import { getSettings } from 'app/React/Helpers/StepHelper'
import * as _ from 'lodash/fp'
import { pipe } from 'ts-pipe-compose'
import PropTypes from 'prop-types'
import '@reach/tooltip/styles.css'
import { usePopper } from 'react-popper'
import './style.scss'
import { AccessoryOption } from 'app/React/Components/Form/AccessoryOption'
import { FeatureFlag } from 'utils/feature-flag'
import IsNotDealerConfigurator from 'app/React/Components/misc/IsNotDealerConfigurator'
import { sameWidth } from 'app/React/Helpers/OptionHelper'
import { ConfiguratorContext } from "app/React/ConfiguratorContainer";

export default function Option(props) {
  const {
    cartItems,
    colorSettingId,
    currentEngineTransmission,
    currentVariant,
    identifier,
    order,
    strings,
    setColorImage,
    setColorName,
    setInteriorImage,
    setOrder,
    defaultValidateSteps
  } = useContext(ConfiguratorContext)

  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const [arrowElement, setArrowElement] = useState(null)

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'left',
    strategy: 'fixed',
    modifiers: [
      { name: 'preventOverflow', options: { padding: 16 } },
      {
        name: 'offset',
        options: {
          offset: [0, 8]
        }
      },
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'top'],
          padding: { bottom: 80 }
        }
      },
      sameWidth
    ]
  })

  const fromString = strings.misc?.from ?? 'From'
  const depositLabel = strings.misc?.security_deposit ?? 'Udbetaling'

  const option = props.option
  const inputName = 'option' + props.settingid
  const inputValue = option.id

  const _setColorImage = useCallback(
    function (image) {
      const colorName = pipe(
        getSettings(currentVariant),
        _.find((s) => s.id === colorSettingId),
        _.get('options'),
        _.values,
        _.find((o) => o.id === option.id),
        _.get('name')
      )
      setColorName(colorName)
      setColorImage(image)
    },
    [setColorImage, setColorName, colorSettingId, currentVariant, identifier, option.id]
  )

  const updateColorImage = useCallback(
    function (optionId) {
      const image = getColorImage(currentVariant, optionId)
      _setColorImage(image)
    },
    [currentVariant, _setColorImage]
  )

  const disabled = pipe(
    option.option_selectable_rules_option,
    _.castArray,
    _.filter(Boolean),
    _.all((rule) =>
      pipe(
        order.configuration,
        _.omit(['variants', 'engineTransmission']),
        Object.values,
        _.some(_.propEq('id', rule)),
        (value) => {
          return option.option_selectable_rules_is_selected === '0' ? !value : value
        },
        Boolean
      )
    ),
    _.negate(Boolean)
  )

  const hidden = option.hide_on === order.paymentType || option.hide_on === 'all'

  const onClick = function () {
    if (disabled) return

    if (
      typeof order.configuration[props.settingid] === 'object' &&
      order.configuration[props.settingid] !== null &&
      order.configuration[props.settingid].id === props.option.id
    ) {
      // User clicked the already chosen option!
      if (props.required && props.required !== '0') {
        setInteriorImage(order.configuration[props.settingid])
        return
      } // This setting is required so don't de-select it
      updateValue(true)
    }
  }

  const updateValue = useCallback(
    function (shouldDelete, option = props.option) {
      if (hidden) return

      if (typeof shouldDelete !== 'boolean') shouldDelete = false

      if (typeof props.onChange === 'function' && !shouldDelete) {
        const shouldStillupdate = props.onChange(props.settingid, option)
        if (shouldStillupdate !== true) return
      }

      const _configuration = Env.get('order.configuration')
      const newConfiguration = shouldDelete
        ? _.omit(props.settingid, _configuration)
        : _.set(props.settingid, option, _configuration)
      setConfiguration(newConfiguration)

      order.configuration = newConfiguration
      order.price = getCurrentPrice(order.paymentType, order, currentVariant, cartItems)
      order.security_deposit = getSecurityDeposit(order.paymentType, order, currentVariant, currentEngineTransmission, cartItems)
      setOrder({ ...order })

      if (shouldDelete) {
        props.setChecked(null)
      } else {
        props.setChecked(option.id)
      }
      if (colorSettingId === props.settingid) {
        updateColorImage(shouldDelete ? null : option.id)
      }

      setInteriorImage(shouldDelete ? null : newConfiguration[props.settingid])

      defaultValidateSteps()
    },
    [
      colorSettingId,
      order.configuration,
      hidden,
      identifier,
      order,
      order.paymentType,
      props,
      setOrder,
      updateColorImage,
      defaultValidateSteps
    ]
  )

  const htmlId = 'option' + props.settingid + '_' + props.option.id + (props.htmlIdPostfix ? '_' + props.htmlIdPostfix : '')

  const displayPrice = function () {
    if (props.hidePrice) {
      return null
    }

    if (order.paymentType !== 'leasing' || option.use_leasing_price === '0') {
      if (
        typeof props.option.min_price !== 'undefined'
      ) {
        return (
          <IsNotDealerConfigurator>
            <div>
              <span>{fromString}</span> <Price price={props.option.min_price}/>
            </div>
          </IsNotDealerConfigurator>
        )
      }
      if (typeof props.option.min_price !== 'undefined') {
        return (
          <IsNotDealerConfigurator>
            <span>{fromString}</span>
            <Price price={props.option.min_price}/>
          </IsNotDealerConfigurator>
        )
      }
      if (typeof props.option.price !== 'undefined') {
        return (
          <IsNotDealerConfigurator>
            <span>+</span>
            <Price price={props.option.price} format='cash'/>
          </IsNotDealerConfigurator>
        )
      }
    } else {
      if (
        typeof props.option.security_deposit_leasing !== 'undefined' &&
        typeof props.option.min_price_leasing !== 'undefined'
      ) {
        return (
          <IsNotDealerConfigurator>
            <div>
              <span>{depositLabel}</span> <Price bold price={props.option.security_deposit_leasing} format='cash'/>
            </div>
            <div>
              <span>{fromString}</span> <Price bold price={props.option.min_price_leasing}/>
            </div>
          </IsNotDealerConfigurator>
        )
      }
      if (typeof props.option.min_price_leasing !== 'undefined') {
        return (
          <IsNotDealerConfigurator>
            <span>{fromString}</span> <Price price={props.option.min_price_leasing}/>
          </IsNotDealerConfigurator>
        )
      }
      if (typeof props.option.price_leasing !== 'undefined') {
        return (
          <IsNotDealerConfigurator>
            <span>+</span>
            <Price price={props.option.price_leasing}/>
          </IsNotDealerConfigurator>
        )
      }
    }
    return null
  }

  useEffect(() => {
    if (disabled) {
      if (order.configuration[props.settingid]?.id == option.id) {
        let newConfiguration = order.configuration
        delete newConfiguration[props.settingid]

        order.configuration = newConfiguration
        setOrder({ ...order })

        defaultValidateSteps()
      }
    }
  }, [order.configuration])

  if (props.option.type === 'accessory-product') {
    if (!FeatureFlag.accessoryShop()) {
      return null
    }
    return <AccessoryOption {...props} />
  }

  if (hidden) {
    return null
  }

  // endregion
  return (
    <>
      <input
        id={htmlId}
        type='radio'
        name={inputName}
        value={inputValue}
        checked={props.checked === props.option.id}
        data-checked={props.checked === props.option.id}
        onChange={updateValue}
        onClick={onClick}
        disabled={disabled}
        required={props.required}
      />
      <label
        ref={setReferenceElement}
        htmlFor={htmlId}
        className='option'
        layout={props.layout}
        data-disabled={disabled}
      >
        {option.color_hex.substr(0, 1) === '#' ? (
          <span className='color' style={{ backgroundColor: option.color_hex }}/>
        ) : null}
        {typeof option.image_url === 'string' && option.image_url.length >= 0 ? (
          <img className='image' src={option.image_url} alt=''/>
        ) : null}
        {typeof option.top_description === 'string' && option.top_description.length >= 0 ? (
          <div className='description model-year'>
              <div>{option.top_description}</div>
          </div>
        ) : null}
        <div>{option.title?.length > 0 ? option.title : option.name}</div>
        {option.description_override ? (
          <div className='description' dangerouslySetInnerHTML={{ __html: option.description_override }}/>
        ) : (
          <div className='description'>
            {displayPrice()}
            {option?.description_title?.length > 0 && option?.description?.length > 0 ? (
              <InlineTip button='Ok' label={option.description_title} content={option.description}/>
            ) : null}
            {option?.description_title?.length > 0 && !option?.description?.length > 0 ? (
              <span dangerouslySetInnerHTML={{ __html: option.description_title }}/>
            ) : null}
          </div>
        )}
      </label>
      {disabled && option.tooltip_unselectable ? (
        <div className='tooltip2' ref={setPopperElement} style={styles.popper} {...attributes.popper}>
          <div dangerouslySetInnerHTML={{ __html: option.tooltip_unselectable }}/>
          <div className='arrow' ref={setArrowElement} style={styles.arrow}/>
        </div>
      ) : null}
    </>
  )
}

Option.defaultProps = {
  onChange: null,
  hide_on: 'none',
  hidePrice: false
}
Option.propTypes = {
  layout: PropTypes.any,
  checked: PropTypes.any,
  settingid: PropTypes.any,
  htmlIdPostfix: PropTypes.any,
  setChecked: PropTypes.any,
  onChange: PropTypes.any,
  required: PropTypes.any,
  hidePrice: PropTypes.any,

  option: PropTypes.shape({
    id: PropTypes.any,
    name: PropTypes.any,
    color_hex: PropTypes.any,
    image_url: PropTypes.any,
    min_price_leasing: PropTypes.any,
    security_deposit_leasing: PropTypes.any,
    variant_index: PropTypes.any,
    price: PropTypes.any,
    min_price: PropTypes.any,
    use_leasing_price: PropTypes.any,
    option_selectable_rules_option: PropTypes.any,
    option_selectable_rules_is_selected: PropTypes.any,
    hide_on: PropTypes.any,
    price_leasing: PropTypes.any,
    title: PropTypes.any,
    description: PropTypes.any,
    description_override: PropTypes.any,
    description_title: PropTypes.any
  })
}
